import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, QrCode, MousePointerClick, AlertCircle, Zap, Eye, ChevronDown, CheckSquare, Copy, Trash2 } from 'lucide-react';
import { Button } from '../../components/Button';
import { QRCodeTable } from '../../components/dashboard/QRCodeTable';
import { StatCard } from '../../components/StatCard';
import { CreateQRCodeModal } from '../../components/qr/CreateQRCodeModal';
import { qrApi } from '../../api/services/qr.service';
import { subscriptionService } from '../../api/services';
import { analytics } from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { sampleQRCodeListEntry, sampleAnalytics, SAMPLE_QR_SHORT_CODE } from '../../data/sampleQrCode';
import { copyToClipboard } from '../../utils/clipboard';
import { getQrTargetUrl } from '../../utils/qr';
import toast from 'react-hot-toast';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from '../../components/ui/alert-dialog';

export function QRCodeDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Treat undefined (stale cached user, pre-migration) as visible, same as the backend default.
  const showSampleDashboard = user?.show_sample_dashboard !== false;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [scanCounts, setScanCounts] = useState<Record<string, number>>({});
  const [isLoadingScans, setIsLoadingScans] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [selecting, setSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleteIds, setDeleteIds] = useState<number[]>([]);
  const selectionControlRef = useRef<HTMLDivElement>(null);
  const deleteControlRef = useRef<HTMLDivElement>(null);
  const restoreSelectionFocus = useRef(false);

  const clearSelection = () => setSelectedIds(new Set());
  useEffect(() => {
    setSelectedIds(new Set());
    setSelecting(false);
    setPage(1);
    setDeleteIds([]);
  }, [user?.id]);

  // Fetch usage stats
  const { data: usage } = useQuery({
    queryKey: ['usage-stats'],
    queryFn: subscriptionService.getUsageStats,
  });

  const canCreate = usage?.can_create_qr ?? true;

  // Fetch QR codes
  const { data: qrCodesResponse, isLoading, isFetching, isError } = useQuery({
    queryKey: ['qr-codes', { search: searchTerm, type: selectedType, page, userId: user?.id }],
    queryFn: () => qrApi.getQRCodes({
      search: searchTerm || undefined,
      has_rules: selectedType === 'rules' ? true : undefined,
      page,
      per_page: 50
    }),
  });

  const qrCodes = React.useMemo(() => qrCodesResponse?.items || [], [qrCodesResponse]);
  const selectableCodes = qrCodes.filter(code => code.id > 0 && code.short_code !== SAMPLE_QR_SHORT_CODE);
  const selectedCodes = selectableCodes.filter(code => selectedIds.has(code.id));
  const totalPages = Math.max(1, qrCodesResponse?.total_pages || 1);
  useEffect(() => {
    if (qrCodesResponse && page > Math.max(1, qrCodesResponse.total_pages)) {
      setPage(Math.max(1, qrCodesResponse.total_pages));
      setSelectedIds(new Set());
    }
  }, [qrCodesResponse, page]);

  const bulkMutation = useMutation({
    mutationFn: ({ ids, action }: { ids: number[]; action: 'activate' | 'deactivate' | 'delete' }) => qrApi.bulkAction(ids, action),
    onSuccess: async (result) => {
      const verb = { activate: 'activated', deactivate: 'deactivated', delete: 'deleted' }[result.action];
      toast.success(`${result.affected_count} QR code${result.affected_count === 1 ? '' : 's'} ${verb}`);
      clearSelection();
      restoreSelectionFocus.current = result.action === 'delete';
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['qr-codes'] }),
        queryClient.invalidateQueries({ queryKey: ['qr-codes-dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['qr-code'] }),
        queryClient.invalidateQueries({ queryKey: ['usage-stats'] }),
      ]);
      setDeleteIds([]);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Bulk action failed. Refresh the list before retrying.');
    },
  });
  const selectionBusy = bulkMutation.isPending || isFetching;
  useEffect(() => {
    if (!selectionBusy && !deleteIds.length && restoreSelectionFocus.current) {
      selectionControlRef.current?.querySelector('button')?.focus();
      restoreSelectionFocus.current = false;
    }
  }, [selectionBusy, deleteIds.length]);
  const runBulkAction = (action: 'activate' | 'deactivate' | 'delete') => {
    if (selectionBusy || !selectedCodes.length) return;
    bulkMutation.mutate({ ids: selectedCodes.map(code => code.id), action });
  };
  const toggleSelected = (id: number) => {
    if (selectionBusy) return;
    setSelectedIds(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectionBusy) return;
    setSelectedIds(current => selectableCodes.every(code => current.has(code.id))
      ? new Set() : new Set(selectableCodes.map(code => code.id)));
  };
  const copySelected = async (targetUrls: boolean) => {
    if (selectionBusy || !selectedCodes.length) return;
    const links = selectedCodes.map(code => targetUrls
      ? (code.fallback_image_id && code.fallback_image_url ? code.fallback_image_url : code.fallback_url)
      : getQrTargetUrl(code));
    try {
      await copyToClipboard(links.join('\n'));
      toast.success(`${links.length} link${links.length === 1 ? '' : 's'} copied`);
    } catch {
      toast.error('Could not copy links. Please try again.');
    }
  };

  // The demo row isn't part of the search/filter query (it's not a real
  // QR code the backend knows about), so only pin it to the top when no
  // search/filter is active — otherwise a "no match" search would still
  // show it, which reads as broken search rather than "here's an example."
  const isUnfiltered = !searchTerm && !selectedType;
  const showSampleRow = showSampleDashboard && isUnfiltered && page === 1;
  const displayQrCodes = showSampleRow ? [sampleQRCodeListEntry, ...qrCodes] : qrCodes;
  const displayScanCounts = showSampleRow
    ? { ...scanCounts, [SAMPLE_QR_SHORT_CODE]: sampleAnalytics.total_scans }
    : scanCounts;

  // Fetch scan counts for all QR codes
  useEffect(() => {
    let cancelled = false;
    const fetchScanCounts = async () => {
      setScanCounts({});
      if (qrCodes.length === 0) {
        setIsLoadingScans(false);
        return;
      }

      setIsLoadingScans(true);
      try {
        const scanPromises = qrCodes.map(qr =>
          qrApi.getAnalyticsSummary(qr.short_code)
            .then(result => ({ short_code: qr.short_code, count: result.total_scans || 0 }))
            .catch(() => ({ short_code: qr.short_code, count: 0 }))
        );

        const results = await Promise.all(scanPromises);
        if (cancelled) return;
        const counts: Record<string, number> = {};
        results.forEach(result => {
          counts[result.short_code] = result.count;
        });

        setScanCounts(counts);

        // Fire qr_first_scan the first time we observe a QR code's scan
        // count go from zero to nonzero. Scans happen server-side (someone
        // scanning the physical code), so this dashboard fetch is the
        // earliest point the owner's frontend session can observe it.
        // sessionStorage dedupes across refetches so it only fires once
        // per QR per browser session.
        results.forEach(({ short_code, count }) => {
          if (count < 1) return;
          const dedupeKey = `ph_qr_first_scan_${short_code}`;
          if (sessionStorage.getItem(dedupeKey)) return;
          sessionStorage.setItem(dedupeKey, '1');
          analytics.capture('qr_first_scan', { short_code });
        });
      } catch (error) {
        console.error('Error fetching scan counts:', error);
      } finally {
        if (!cancelled) setIsLoadingScans(false);
      }
    };

    fetchScanCounts();
    return () => { cancelled = true; };
  }, [qrCodes]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuOpen && !(event.target as Element).closest('.filter-dropdown')) {
        setFilterMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterMenuOpen]);

  const totalScans = Object.values(scanCounts).reduce((sum, count) => sum + count, 0);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
        <div>
          <h1 className="heading-1 mb-2">QR Codes</h1>
          <p className="text-[--clinqr-text-secondary]">Manage and monitor your QR code campaigns</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div ref={selectionControlRef}><Button
            variant="secondary"
            disabled={selectionBusy || (!selecting && selectableCodes.length === 0)}
            onClick={() => { setSelecting(!selecting); clearSelection(); }}
          >
            <CheckSquare className="w-4 h-4" />
            {selecting ? 'Cancel selection' : 'Select'}
          </Button></div>
          <Button
          variant="primary"
          onClick={() => setIsCreateModalOpen(true)}
          disabled={!canCreate || bulkMutation.isPending}
          title={!canCreate ? "Active QR code limit reached" : ""}
        >
          <Plus className="w-4 h-4" />
          Create QR Code
        </Button>
        </div>
      </div>

      {/* Limit Warning */}
      {!canCreate && (
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <p className="text-amber-800 text-sm">
              <span className="font-bold">Limit Reached:</span> You've used all {usage?.qr_code_limit} active QR code slots in your current plan. Deactivate a code to free a slot.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard/billing')}
            className="text-amber-700 hover:text-amber-800 hover:bg-amber-100"
          >
            <Zap className="w-3 h-3" />
            Upgrade Plan
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <StatCard
          value={(qrCodesResponse?.total || 0).toString()}
          label={isUnfiltered ? 'Total QR Codes' : 'Matching QR Codes'}
          icon={QrCode}
          className="bg-[--clinqr-bg-subtle]/30 border-[--clinqr-border]/5"
        />
        <StatCard
          value={isLoadingScans ? '...' : totalScans.toLocaleString()}
          label="Scans on This Page"
          icon={MousePointerClick}
          className="bg-[--clinqr-bg-subtle]/30 border-[--clinqr-border]/5"
        />
        <StatCard
          value={qrCodes.filter(q => q.is_active).length.toString()}
          label="Active on This Page"
          icon={Eye}
          className="bg-[--clinqr-bg-subtle]/30 border-[--clinqr-border]/5"
        />
        <StatCard
          value={qrCodes.filter(q => !q.is_active).length.toString()}
          label="Inactive on This Page"
          icon={QrCode}
          className="bg-[--clinqr-bg-subtle]/30 border-[--clinqr-border]/5"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[--clinqr-text-secondary]" />
            <input
              type="text"
              placeholder="Search QR codes..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); clearSelection(); }}
              disabled={bulkMutation.isPending}
              className="w-full h-12 pl-10 pr-4 rounded-xl border border-[--clinqr-border] focus:outline-none focus:border-[--clinqr-primary] bg-white text-[--clinqr-text-primary] placeholder-[--clinqr-text-secondary]"
            />
          </div>
        </div>
        <div className="relative filter-dropdown">
          <Button
            variant="secondary"
            disabled={bulkMutation.isPending}
            onClick={() => setFilterMenuOpen(!filterMenuOpen)}
            className="h-12 px-4 flex items-center gap-2 min-w-[180px]"
          >
            {selectedType === 'rules' ? 'With Rules' : 'All QR Codes'}
            <ChevronDown className={`w-4 h-4 transition-transform ${filterMenuOpen ? 'rotate-180' : ''}`} />
          </Button>
          {filterMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-[--clinqr-border] z-50">
              <button
                disabled={bulkMutation.isPending}
                onClick={() => {
                  setSelectedType('');
                  setPage(1);
                  clearSelection();
                  setFilterMenuOpen(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-[--clinqr-bg-subtle] ${
                  selectedType === '' ? 'bg-[--clinqr-primary]/10 text-[--clinqr-primary]' : ''
                }`}
              >
                All QR Codes
              </button>
              <button
                disabled={bulkMutation.isPending}
                onClick={() => {
                  setSelectedType('rules');
                  setPage(1);
                  clearSelection();
                  setFilterMenuOpen(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-[--clinqr-bg-subtle] ${
                  selectedType === 'rules' ? 'bg-[--clinqr-primary]/10 text-[--clinqr-primary]' : ''
                }`}
              >
                With Rules
              </button>
            </div>
          )}
        </div>
      </div>

      {selecting && (
        <div aria-label="Bulk actions" role="region" className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-[--clinqr-border] bg-white p-4">
          <span role="status" className="mr-auto text-sm font-semibold text-[--clinqr-text-primary]">
            {bulkMutation.isPending ? 'Updating QR codes…' : `${selectedCodes.length} selected on this page`}
          </span>
          <Button size="sm" variant="secondary" disabled={selectionBusy || !selectedCodes.length} onClick={() => runBulkAction('activate')}>Make active</Button>
          <Button size="sm" variant="secondary" disabled={selectionBusy || !selectedCodes.length} onClick={() => runBulkAction('deactivate')}>Make inactive</Button>
          <Button size="sm" variant="ghost" disabled={selectionBusy || !selectedCodes.length} onClick={() => copySelected(false)}><Copy className="w-4 h-4" />Copy Clinqr links</Button>
          <Button size="sm" variant="ghost" disabled={selectionBusy || !selectedCodes.length} onClick={() => copySelected(true)}>Copy target URLs</Button>
          <div ref={deleteControlRef}><Button size="sm" variant="danger" disabled={selectionBusy || !selectedCodes.length} onClick={() => setDeleteIds(selectedCodes.map(code => code.id))}><Trash2 className="w-4 h-4" />Delete</Button></div>
        </div>
      )}

      {/* QR Codes Table */}
      {isError ? (
        <p role="alert" className="py-8 text-center">Could not load QR codes. Please refresh to try again.</p>
      ) : isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[--clinqr-primary] mx-auto mb-4"></div>
          <p className="text-[--clinqr-text-secondary]">Loading QR codes...</p>
        </div>
      ) : displayQrCodes.length === 0 ? (
        <div className="bg-white rounded-xl border border-[--clinqr-border]/10 overflow-hidden shadow-sm">
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-[--clinqr-bg-subtle] rounded-full flex items-center justify-center mx-auto mb-6">
              <QrCode className="w-12 h-12 text-[--clinqr-text-secondary]" strokeWidth={1} />
            </div>
            <h3 className="text-xl font-semibold text-[--clinqr-text-primary] mb-2">No QR codes yet</h3>
            <p className="text-[--clinqr-text-secondary] mb-8 max-w-md mx-auto">
              Create your first QR code to start tracking engagement and managing your campaigns
            </p>
            <Button
              variant="primary"
              onClick={() => setIsCreateModalOpen(true)}
              className="mx-auto"
            >
              Create Your First QR Code
            </Button>
          </div>
        </div>
      ) : (
        <QRCodeTable
          qrCodes={displayQrCodes}
          scanCounts={displayScanCounts}
          isLoadingScans={isLoadingScans}
          selection={selecting ? { selectedIds, onToggle: toggleSelected, onToggleAll: toggleAll, disabled: selectionBusy } : undefined}
        />
      )}

      {qrCodesResponse && totalPages > 1 && (
        <nav aria-label="QR code pages" className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[--clinqr-text-secondary]">Page {page} of {totalPages} · {qrCodesResponse.total} QR codes</p>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" disabled={selectionBusy || page <= 1} onClick={() => { setPage(page - 1); clearSelection(); }}>Previous</Button>
            <Button size="sm" variant="secondary" disabled={selectionBusy || page >= totalPages} onClick={() => { setPage(page + 1); clearSelection(); }}>Next</Button>
          </div>
        </nav>
      )}

      <CreateQRCodeModal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
      <AlertDialog open={deleteIds.length > 0} onOpenChange={open => { if (!open && !bulkMutation.isPending) setDeleteIds([]); }}>
        <AlertDialogContent
          className="bg-white text-[--clinqr-text-primary]"
          onCloseAutoFocus={event => {
            event.preventDefault();
            if (!restoreSelectionFocus.current) deleteControlRef.current?.querySelector('button')?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteIds.length} QR code{deleteIds.length === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              These QR codes and their scan history will be permanently deleted. Their Clinqr links will stop working. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={selectionBusy}
              onClick={event => {
                event.preventDefault();
                if (!selectionBusy && deleteIds.length) bulkMutation.mutate({ ids: deleteIds, action: 'delete' });
              }}
              className="bg-[--clinqr-error] text-white"
            >
              {bulkMutation.isPending ? 'Deleting…' : 'Delete QR codes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
